/**
 * Brand name normalizer.
 *
 * The brand is written "Electrolibre" — one word, no accent — but the content
 * across the store (metafields, apps, translations, old sections) is full of
 * variants: "Électro Libre", "Electro-Libre", "ELECTRO LIBRE", non-breaking
 * spaces, and so on. This rewrites those variants in place, on text nodes only,
 * so surrounding markup and styling are untouched.
 *
 * Case is preserved per match: ALL CAPS stays caps, Capitalised stays
 * capitalised, lowercase stays lowercase.
 *
 * Opt an element (and its subtree) out with data-keep-brand-name.
 */
(function () {
  'use strict';

  // "E" in any accented form, "lectro", an optional separator, then "libre".
  var PATTERN = /[EÉÈÊËeéèêë]lectro[\s  ‑–-]*libre/gi;

  var SKIP_TAGS = {
    SCRIPT: 1, STYLE: 1, NOSCRIPT: 1, TEMPLATE: 1, TEXTAREA: 1,
    CODE: 1, PRE: 1, SVG: 1, IFRAME: 1, OBJECT: 1
  };

  // Attributes worth fixing: they surface as visible text or are read out.
  var ATTRIBUTES = ['alt', 'title', 'placeholder', 'aria-label', 'value'];
  var ATTRIBUTE_SELECTOR = '[alt],[title],[placeholder],[aria-label],input[type="submit"][value],input[type="button"][value]';

  function replacement(match) {
    var letters = match.replace(/[^A-Za-zÀ-ÿ]/g, '');
    if (letters === letters.toUpperCase()) return 'ELECTROLIBRE';
    if (match[0] === match[0].toUpperCase()) return 'Electrolibre';
    return 'electrolibre';
  }

  function fix(text) {
    PATTERN.lastIndex = 0;
    return text.replace(PATTERN, replacement);
  }

  function needsFix(text) {
    PATTERN.lastIndex = 0;
    return PATTERN.test(text);
  }

  function isSkipped(node) {
    for (var el = node.parentElement; el; el = el.parentElement) {
      if (SKIP_TAGS[el.nodeName]) return true;
      if (el.hasAttribute && el.hasAttribute('data-keep-brand-name')) return true;
    }
    return false;
  }

  function walkText(root) {
    if (root.nodeType === Node.TEXT_NODE) {
      if (needsFix(root.nodeValue) && !isSkipped(root)) root.nodeValue = fix(root.nodeValue);
      return;
    }
    if (root.nodeType !== Node.ELEMENT_NODE && root.nodeType !== Node.DOCUMENT_FRAGMENT_NODE) return;

    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: function (node) {
        if (!needsFix(node.nodeValue)) return NodeFilter.FILTER_REJECT;
        return isSkipped(node) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT;
      }
    });
    var pending = [];
    while (walker.nextNode()) pending.push(walker.currentNode);
    for (var i = 0; i < pending.length; i++) pending[i].nodeValue = fix(pending[i].nodeValue);
  }

  function walkAttributes(root) {
    if (root.nodeType !== Node.ELEMENT_NODE) return;
    var elements = root.matches(ATTRIBUTE_SELECTOR) ? [root] : [];
    elements = elements.concat(Array.prototype.slice.call(root.querySelectorAll(ATTRIBUTE_SELECTOR)));

    for (var i = 0; i < elements.length; i++) {
      var el = elements[i];
      if (el.closest('[data-keep-brand-name]')) continue;
      for (var a = 0; a < ATTRIBUTES.length; a++) {
        var name = ATTRIBUTES[a];
        var current = el.getAttribute(name);
        if (current && needsFix(current)) el.setAttribute(name, fix(current));
      }
    }
  }

  function normalize(root) {
    walkText(root);
    walkAttributes(root);
  }

  function run() {
    normalize(document.body);
    if (document.title && needsFix(document.title)) document.title = fix(document.title);

    if (!('MutationObserver' in window)) return;

    // Cart drawer, predictive search, quick view, app embeds: anything injected
    // after load gets the same treatment.
    //
    // characterData is deliberately not observed: in-place text edits are rare
    // (apps replace nodes, which childList catches) and watching them would fire
    // on every countdown tick. Added nodes are queued and flushed once per frame
    // so a burst of injections costs one pass, not one per mutation.
    var queue = [];
    var scheduled = false;

    function flush() {
      scheduled = false;
      var batch = queue;
      queue = [];
      for (var i = 0; i < batch.length; i++) {
        var node = batch[i];
        // Skip nodes detached again before the flush ran.
        if (node.isConnected) normalize(node);
      }
    }

    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var n = 0; n < added.length; n++) queue.push(added[n]);
      }
      if (queue.length && !scheduled) {
        scheduled = true;
        requestAnimationFrame(flush);
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
